import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import InfoCard from "connectui/component/display/card/InfoCard";
import WarningCardBase from "connectui/component/display/card/base/WarningCardBase";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";
import Container from "../../../../connectui/component/layout/Container";
import {
  BaseChartTooltip,
  SegmentedBarChart,
} from "../../../../connectui/component/charts";
import LineChart from "../../../../connectui/component/charts/LineChart";
import OutlinedButtonBase from "../../../../connectui/component/input/button/base/OutlinedButtonBase";
import ButtonSegmentedGroup from "../../../../connectui/component/input/button/ButtonSegmentedGroup";
import VirtualizedListComponent from "../../../../connectui/component/layout/list/VirtualizedListComponent";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../connectui/icons/iconMap";
import ModalBase from "../../../../connectui/component/modal/ModalBase";
import ProfilePicture from "../../../../connectui/component/display/ProfilePicture";
import Skeleton from "../../../../connectui/component/display/Skeleton";
import Tooltip from "../../../../connectui/component/display/Tooltip";
import TextInput from "../../../../connectui/component/input/text/type/TextInput";
import OutlinedInputBase from "../../../../connectui/component/input/text/OutlinedInputBase";
import {getDashboardResponseTime} from "redux/actions/dashboardActions";
import ButtonIconText from "../../../../connectui/component/input/button/ButtonIconText";
import EmptyButtonBase from "../../../../connectui/component/input/button/base/EmptyButtonBase";
import ButtonIcon from "../../../../connectui/component/input/button/ButtonIcon";
import {editAccount} from "redux/actions/accountsActions";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import ButtonText from "connectui/component/input/button/ButtonText";
import TimeframeSelector, {INTERVAL_MAP} from "../input/TimeframeSelector";
import {useTranslation} from "react-i18next";
import Divider from "../../../../connectui/component/display/Divider";
import {exportResponseTimeData} from "../../../../utilities/helperFunctions";
import OutlinedCardBase from "../../../../connectui/component/display/card/base/OutlinedCardBase";
const ResponseTimeCard = ({timeframe}) => {
  const {theme, isMobile} = useTheme();
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const userProfile = useSelector((state) => state.defaultReducer.user_profile);
  const currentUser = useSelector((state) => state.defaultReducer.current_user);

  const [selectedView, setSelectedView] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [localPinnedUsers, setLocalPinnedUsers] = useState([]);
  const [displayInterval, setDisplayInterval] = useState("By Day");
  const [searchText, setSearchText] = useState("");

  const [cardData, setCardData] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const pinnedUsers = useMemo(
    () => userProfile?.dashboard_settings?.response_time_pinned_users || [],
    [userProfile],
  );

  const getContainerHeight = (isModal, isNoResults = false) => {
    if (!isModal) return "400px";
    if (isNoResults && !isMobile) return "547px";
    return isMobile ? "300px" : "500px";
  };

  /**
   * Data Availability Warning Component using InfoCard
   * @returns {JSX.Element|null}
   */
  const DataAvailabilityWarning = () => {
    if (isMobile) return null;
    if (timeframe?.startDate > 1749474000000) return null;

    return (
      <InfoCard
        cardBase={WarningCardBase}
        showBorder={true}
        title={"Warning"}
        description={t("dashboard.response-time.data-availability-warning")}
        sx={{
          margin: theme.spacing(0, 0, 3, 0),
          width: "100%",
        }}
      />
    );
  };

  const renderEmptyState = (height = "400px", isModal = false) => (
    <DashboardEmptyStateCard
      text={t("dashboard.response-time.empty-state")}
      icon={ICONS["no-messages-white"]}
      action={
        !isModal ? (
          <ButtonText
            label="Add Members"
            onClick={handleViewMore}
            as="div"
            color={theme.colors.white}
          />
        ) : null
      }
      height={height}
    />
  );

  const renderTimeframeSelector = () => (
    <TimeframeSelector
      timeframe={timeframe}
      selectedInterval={displayInterval}
      onIntervalChange={setDisplayInterval}
      sx={{width: "140px"}}
    />
  );

  const renderCloseButton = () => (
    <Container sx={{width: "14px", height: "14px"}}>
      <SvgIcon
        component={ICONS.x}
        onClick={handleModalClose}
        sx={{
          cursor: "pointer",
          width: "14px",
          height: "14px",
          color: theme.colors.gray500,
        }}
      />
    </Container>
  );

  const renderSearchInput = () => (
    <Container sx={{width: "250px", flexShrink: 0}}>
      <TextInput
        inputBase={OutlinedInputBase}
        startIcon={
          <SvgIcon
            component={ICONS.search}
            sx={{
              width: "16px",
              height: "16px",
              color: theme.colors.gray500,
              marginRight: theme.spacing(1),
            }}
          />
        }
        inputProps={{
          placeholder: "Search",
          value: searchText,
          onChange: (e) => setSearchText(e.target.value),
        }}
        actions={
          !!searchText && (
            <SvgIcon
              component={ICONS.x}
              sx={{
                width: "11px",
                height: "11px",
                cursor: "pointer",
                color: theme.colors.gray500,
              }}
              onClick={() => setSearchText("")}
            />
          )
        }
        sx={{
          padding: theme.spacing(1, 2),
          height: "32px",
        }}
      />
    </Container>
  );

  const renderViewSegmentedGroup = () => {
    return (
      <Container sx={{width: "80px", flexShrink: 0}}>
        <ButtonSegmentedGroup
          selectedIndex={selectedView}
          onChange={setSelectedView}
          sx={{
            height: `calc(${theme.spacing(3)} + 1.25rem)`,
            width: "100%",
          }}
        >
          <ButtonIcon
            icon={
              <SvgIcon
                component={ICONS.menu}
                sx={{
                  width: "16px !important",
                  height: "16px !important",
                  color: theme.colors.textSecondary,
                }}
              />
            }
            as="div"
          />
          <ButtonIcon
            icon={
              <SvgIcon
                component={ICONS.users}
                sx={{
                  width: "16px !important",
                  height: "16px !important",
                  color: theme.colors.textSecondary,
                }}
              />
            }
            as="div"
          />
        </ButtonSegmentedGroup>
      </Container>
    );
  };

  const handleExport = () => {
    const viewType = selectedView === 0 ? "timeseries" : "users";

    exportResponseTimeData({
      viewType,
      cardData,
      modalData,
      userProfile,
      pinnedUsers,
    });
  };

  const renderControls = (isModal = false) => (
    <FlexContainer
      alignItems="center"
      gap={isModal ? theme.spacing(3) : theme.spacing(2)}
    >
      {!isMobile && selectedView === 0 && renderTimeframeSelector()}
      {isModal && selectedView === 1 && !isMobile && renderSearchInput()}
      {isModal && !isMobile && (
        <ButtonIconText
          buttonBase={OutlinedButtonBase}
          label="Export"
          typographyVariant="textSM"
          onClick={handleExport}
          color={theme.colors.textSecondary}
          as="div"
          sx={{height: "32px"}}
          icon={
            <SvgIcon
              component={ICONS.download}
              sx={{
                width: "16px",
                height: "16px",
                color: theme.colors.textSecondary,
                marginRight: theme.spacing(1),
              }}
            />
          }
        />
      )}
      {renderViewSegmentedGroup()}
      {isModal && renderCloseButton()}
    </FlexContainer>
  );

  const formatResponseTime = (minutes) => {
    const numValue = parseFloat(minutes) || 0;

    if (numValue === 0) return "0m";

    const days = Math.floor(numValue / (24 * 60));
    const hours = Math.floor((numValue % (24 * 60)) / 60);
    const remainingMinutes = numValue % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }

    if (hours > 0) {
      parts.push(`${hours}h`);
    }

    if (remainingMinutes > 0 || parts.length === 0) {
      const formattedMinutes = parseFloat(remainingMinutes.toFixed(1));
      parts.push(`${formattedMinutes}m`);
    }

    return parts.join(" ");
  };

  const ResponseTimeCardTooltip = ({data, position, chartContainerRef}) => {
    if (!data || !data.items || !position) return null;

    const formattedItems = data.items.map((item) => ({
      ...item,
      value: formatResponseTime(item.value),
    }));

    return (
      <BaseChartTooltip
        position={position}
        color={data.items[0]?.color}
        items={formattedItems}
        chartContainerRef={chartContainerRef}
      />
    );
  };

  const fetchCardData = (pinnedUsersList = null) => {
    if (!timeframe?.startDate || !timeframe?.endDate || !userProfile) {
      return;
    }

    const usersToPin = pinnedUsersList || pinnedUsers;

    if (usersToPin.length === 0) {
      return;
    }

    setCardLoading(true);

    dispatch(
      getDashboardResponseTime({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: null,
        timeseries_user_ids: usersToPin,
        summary_user_ids: usersToPin,
        interval: INTERVAL_MAP[displayInterval],
        onSuccess: (response) => {
          setCardData(response);
          setCardLoading(false);
        },
        onError: () => {
          setCardLoading(false);
        },
      }),
    );
  };

  const fetchModalData = () => {
    if (!timeframe?.startDate || !timeframe?.endDate) {
      return;
    }

    setModalLoading(true);

    dispatch(
      getDashboardResponseTime({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: null,
        summary_user_ids: null,
        interval: INTERVAL_MAP[displayInterval],
        onSuccess: (response) => {
          setModalData(response);
          setModalLoading(false);
        },
        onError: () => setModalLoading(false),
      }),
    );
  };

  useEffect(() => {
    if (
      !timeframe?.startDate ||
      !timeframe?.endDate ||
      !userProfile?.enso_user_id
    ) {
      return;
    }

    if (pinnedUsers.length === 0) {
      setCardData(null);
      setCardLoading(false);
      return;
    }

    if (!modalOpen) {
      fetchCardData();
    }
  }, [
    timeframe?.startDate,
    timeframe?.endDate,
    userProfile?.enso_user_id,
    pinnedUsers,
  ]);

  useEffect(() => {
    if (modalOpen) {
      fetchModalData();
    }
  }, [modalOpen, timeframe?.startDate, timeframe?.endDate]);

  useEffect(() => {
    if (pinnedUsers.length > 0) {
      fetchCardData();
    }
  }, [displayInterval]);

  useEffect(() => {
    if (modalOpen && selectedView === 0) {
      const pinnedChanged =
        JSON.stringify(localPinnedUsers.sort()) !==
        JSON.stringify(pinnedUsers.sort());

      if (pinnedChanged) {
        setCardLoading(true);
      }

      updatePinnedUsers(() => {
        if (localPinnedUsers.length > 0) {
          fetchCardData(localPinnedUsers);
        }
      });
    }
  }, [modalOpen, selectedView]);

  const processUsers = (data, useModalData = false) => {
    if (!data?.users) return [];

    return Object.entries(data.users)
      .map(([userId, userData]) => {
        const user =
          userProfile?.enso_user_id === userId
            ? userProfile
            : userProfile?.enso_users?.find((eu) => eu.enso_user_id === userId);

        const isPinned = useModalData
          ? localPinnedUsers.includes(userId)
          : pinnedUsers.includes(userId);

        return {
          userId,
          userName: user?.name || "Unknown User",
          userProfilePicture: user?.profile_picture,
          averageTimeMinutes: userData.summary?.average_time_minutes || 0,
          messageCount: userData.summary?.resolution_count || 0,
          isPinned,
        };
      })
      .sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;

        const aHasValue = a.averageTimeMinutes > 0;
        const bHasValue = b.averageTimeMinutes > 0;

        if (aHasValue && !bHasValue) return -1;
        if (!aHasValue && bHasValue) return 1;

        if (aHasValue && bHasValue) {
          return a.averageTimeMinutes - b.averageTimeMinutes;
        }

        return 0;
      });
  };

  const cardUsersData = useMemo(
    () => processUsers(cardData, false),
    [cardData, userProfile, pinnedUsers],
  );
  const modalUsersData = useMemo(
    () => processUsers(modalData, true),
    [modalData, userProfile, localPinnedUsers],
  );

  const filteredModalUsersData = useMemo(() => {
    if (!searchText) return modalUsersData;
    return modalUsersData.filter((user) =>
      user.userName.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [modalUsersData, searchText]);

  const maxResponseTime = useMemo(() => {
    if (cardUsersData.length === 0) return 240;
    return Math.max(...cardUsersData.map((user) => user.averageTimeMinutes));
  }, [cardUsersData]);

  const maxModalResponseTime = useMemo(() => {
    if (modalUsersData.length === 0) return 240;
    return Math.max(...modalUsersData.map((user) => user.averageTimeMinutes));
  }, [modalUsersData]);

  const createProgressData = (averageTime, maxTime = maxResponseTime) => {
    const progress = Math.min((averageTime / maxTime) * 100, 100);
    return [
      {id: "completed", value: progress, color: theme.colors.primary},
      {id: "remaining", value: 100 - progress, color: theme.colors.gray200},
    ];
  };

  const renderUserItem = (user, isModal = false) => (
    <FlexContainer
      alignItems="center"
      justifyContent="space-between"
      gap={theme.spacing(1)}
      sx={{
        padding: theme.spacing(2, 0),
        marginRight: theme.spacing(2),
        minWidth: 0,
      }}
    >
      <FlexContainer
        alignItems="center"
        gap={theme.spacing(2)}
        sx={{
          flex: "1 1 0",
          minWidth: "80px",
          overflow: "hidden",
        }}
      >
        {isModal &&
          (() => {
            const isDisabled = !user.isPinned && localPinnedUsers.length >= 8;
            const button = (
              <ButtonIcon
                icon={
                  <SvgIcon
                    component={ICONS.pin}
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: user.isPinned
                        ? theme.colors.primary
                        : isDisabled
                          ? theme.colors.gray300
                          : theme.colors.gray400,
                    }}
                  />
                }
                onClick={() => handlePinToggle(user.userId)}
                disabled={isDisabled}
                sx={{
                  padding: theme.spacing(0.5),
                  backgroundColor: "transparent",
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: isDisabled
                      ? "transparent"
                      : theme.colors.gray100,
                  },
                  "&:disabled": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            );

            return isDisabled ? (
              <Tooltip tooltipContent="Maximum 8 users can be pinned">
                <Container>{button}</Container>
              </Tooltip>
            ) : (
              button
            );
          })()}

        <ProfilePicture
          name={user.userName}
          image={user.userProfilePicture}
          sx={{
            width: "32px",
            height: "32px",
            flexShrink: 0,
          }}
        />

        <Container
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Tooltip
            tooltipContent={user.userName}
            sx={{
              tooltip: {
                maxWidth: "300px",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
              trigger: {
                display: "block",
                width: "100%",
              },
            }}
          >
            <Text
              variant="textSM"
              weight="medium"
              color="textPrimary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "default",
                textAlign: "left",
              }}
            >
              {user.userName}
            </Text>
          </Tooltip>
        </Container>
      </FlexContainer>

      {!isMobile && (
        <Container
          width="100%"
          height={28}
          position="relative"
          sx={{
            flex: 1,
            minWidth: "60px",
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
            padding: theme.spacing(1, 0),
          }}
        >
          <SegmentedBarChart
            data={createProgressData(
              user.averageTimeMinutes,
              isModal ? maxModalResponseTime : maxResponseTime,
            )}
            settings={{
              height: 16,
              borderRadius: theme.radius.xl,
              minBarWidth: 3,
            }}
          />
        </Container>
      )}
      <FlexContainer
        flexDirection="column"
        alignItems="center"
        sx={{
          minWidth: "50px",
          flexShrink: 0,
        }}
      >
        <Tooltip
          tooltipContent={`${formatResponseTime(user.averageTimeMinutes)} avg response time`}
        >
          <Text variant="textSM" weight="semibold" sx={{cursor: "default"}}>
            {formatResponseTime(user.averageTimeMinutes)}
          </Text>
        </Tooltip>
      </FlexContainer>

      <FlexContainer
        flexDirection="column"
        alignItems="center"
        sx={{
          minWidth: "45px",
          flexShrink: 0,
        }}
      >
        <Tooltip tooltipContent={`${user.messageCount} resolutions`}>
          <Text variant="textSM" weight="semibold" sx={{cursor: "default"}}>
            {user.messageCount} res
          </Text>
        </Tooltip>
      </FlexContainer>
    </FlexContainer>
  );

  const renderTotalResponseTime = (totalResponseTime) => {
    if (totalResponseTime === undefined) return null;

    return (
      <FlexContainer
        justifyContent="flex-start"
        sx={{
          paddingBottom: theme.spacing(2),
          marginBottom: theme.spacing(2),
        }}
      >
        <Text variant="displaySM" weight="semibold">
          {formatResponseTime(totalResponseTime)}
        </Text>
      </FlexContainer>
    );
  };

  const handleViewMore = () => {
    setSelectedView(1);
    if (!modalOpen) {
      handleModalOpen();
    }
  };

  const renderLoadingState = (height = "200px", skeletonCount = 4) => (
    <Container sx={{width: "100%", padding: theme.spacing(2, 0)}}>
      {Array.from({length: skeletonCount}).map((_, index) => (
        <Skeleton
          key={index}
          width="100%"
          height={index === 0 ? height : "60px"}
          style={{marginBottom: theme.spacing(2)}}
        />
      ))}
    </Container>
  );

  const renderUsersView = (isModal = false) => {
    const currentUsersData = isModal ? filteredModalUsersData : cardUsersData;
    const currentLoading = isModal ? modalLoading : cardLoading;
    const currentData = isModal ? modalData : cardData;

    if (!isModal && pinnedUsers.length === 0) {
      return renderEmptyState("400px");
    }

    if (currentLoading || (currentData === null && pinnedUsers.length > 0)) {
      return renderLoadingState(isModal ? "70px" : "60px", isModal ? 6 : 4);
    }

    if (currentUsersData.length === 0) {
      if (isModal && searchText && modalUsersData.length > 0) {
        return (
          <Container
            sx={{
              width: "100%",
              padding: theme.spacing(4),
              textAlign: "center",
              minHeight: getContainerHeight(true, true),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text variant="textMD" color="textSecondary">
              {t("dashboard.response-time.no-search-results", {searchText})}
            </Text>
          </Container>
        );
      }
      return renderEmptyState(isModal ? "400px" : "300px", isModal);
    }

    const totalResponseTime = currentData?.total_response_time;

    if (!isModal) {
      return (
        <Container sx={{width: "100%"}}>
          {renderTotalResponseTime(totalResponseTime)}
          {currentUsersData.slice(0, 8).map((user) => (
            <Container key={user.userId}>{renderUserItem(user)}</Container>
          ))}
        </Container>
      );
    }

    return (
      <Container sx={{width: "100%"}}>
        {renderTotalResponseTime(totalResponseTime)}
        <Container sx={{height: getContainerHeight(true), width: "100%"}}>
          <VirtualizedListComponent
            totalRows={currentUsersData.length}
            rowHeight={70}
            isRowLoaded={({index}) => index < currentUsersData.length}
            loadMoreRows={() => Promise.resolve()}
          >
            {(index) =>
              currentUsersData[index]
                ? renderUserItem(currentUsersData[index], true)
                : null
            }
          </VirtualizedListComponent>
        </Container>
      </Container>
    );
  };

  const renderTimeseriesView = (isModal = false) => {
    const currentData = cardData;

    if (pinnedUsers.length === 0) {
      return renderEmptyState("400px");
    }

    if (cardLoading || currentData === null) {
      return renderLoadingState("200px", 1);
    }

    if (!currentData?.dataPoints || currentData.dataPoints.length === 0) {
      return renderEmptyState("200px", isModal);
    }

    const chartHeight = isModal ? (isMobile ? 300 : 400) : 200;
    const totalResponseTime = currentData?.total_response_time;

    const sortedDataPoints = [...currentData.dataPoints].sort((a, b) => {
      const dateA = new Date(a.x);
      const dateB = new Date(b.x);
      return dateA - dateB;
    });

    const seriesWithUserInfo = currentData.series.map((series) => {
      const user =
        userProfile?.enso_user_id === series.id
          ? userProfile
          : userProfile?.enso_users?.find(
              (eu) => eu.enso_user_id === series.id,
            );

      return {
        ...series,
        name: user?.name || "Unknown User",
        color: series.color,
      };
    });

    const filteredTimeseriesSeries =
      pinnedUsers.length > 0
        ? seriesWithUserInfo.filter((series) => pinnedUsers.includes(series.id))
        : seriesWithUserInfo;

    return (
      <Container sx={{width: "100%"}}>
        {renderTotalResponseTime(totalResponseTime)}
        <FlexContainer
          gap={theme.spacing(3)}
          sx={{marginBottom: theme.spacing(3), flexWrap: "wrap"}}
        >
          {filteredTimeseriesSeries.map((series) => (
            <FlexContainer
              key={series.id}
              alignItems="center"
              gap={theme.spacing(1)}
            >
              <Container
                sx={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: series.color,
                }}
              />
              <Text variant="textXS" color="textSecondary">
                {series.name}
              </Text>
            </FlexContainer>
          ))}
        </FlexContainer>

        <Container sx={{height: `${chartHeight}px`, width: "100%"}}>
          <LineChart
            data={sortedDataPoints}
            series={filteredTimeseriesSeries}
            settings={{
              height: chartHeight,
              padding: {top: 20, right: 20, bottom: 20, left: 40},
              tooltip: {
                component: ResponseTimeCardTooltip,
              },
              grid: {
                horizontal: true,
                vertical: false,
                color: theme.colors.gray300,
              },
              axis: {
                x: {
                  tickCount: 6,
                },
                y: {
                  tickCount: 4,
                  label: "Minutes",
                },
              },
            }}
          />
        </Container>
      </Container>
    );
  };

  const renderCardContent = () => (
    <Container sx={{width: "100%"}}>
      <DataAvailabilityWarning />
      {selectedView === 0 ? renderTimeseriesView() : renderUsersView()}
      {!cardLoading &&
        cardData !== null &&
        pinnedUsers.length > 0 &&
        renderBottomBar()}
    </Container>
  );

  const handlePinToggle = (userId) => {
    const isCurrentlyPinned = localPinnedUsers.includes(userId);
    const MAX_PINS = 8;

    if (isCurrentlyPinned) {
      const newLocalPinnedUsers = localPinnedUsers.filter(
        (id) => id !== userId,
      );
      setLocalPinnedUsers(newLocalPinnedUsers);
    } else if (localPinnedUsers.length < MAX_PINS) {
      const newLocalPinnedUsers = [...localPinnedUsers, userId];
      setLocalPinnedUsers(newLocalPinnedUsers);
    }
  };

  const handleModalOpen = () => {
    setLocalPinnedUsers([...pinnedUsers]);
    setSearchText("");
    setModalOpen(true);
  };

  const updatePinnedUsers = (onComplete) => {
    const pinnedChanged =
      JSON.stringify(localPinnedUsers.sort()) !==
      JSON.stringify(pinnedUsers.sort());

    if (pinnedChanged) {
      const body = {
        enso_key: currentUser,
        enso_user_id: userProfile.enso_user_id,
        data: {
          dashboard_settings: {
            ...userProfile.dashboard_settings,
            response_time_pinned_users: localPinnedUsers,
          },
        },
      };

      dispatch(
        editAccount({
          body: {body},
          disableAlerts: true,
          onSuccess: () => {
            if (onComplete) {
              onComplete();
            }
          },
        }),
      );
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSearchText("");
    updatePinnedUsers();
  };

  const renderBottomBar = () => (
    <FlexContainer
      flexDirection={"column"}
      justifyContent="center"
      alignItems={"center"}
    >
      <Divider />
      <ButtonIconText
        buttonBase={EmptyButtonBase}
        label={"View More"}
        onClick={handleViewMore}
        color={theme.colors.primary}
        as="div"
        sx={{
          padding: theme.spacing(1, 2),
          color: theme.colors.primary,
          fontSize: theme.typography.textSM.fontSize,
          fontWeight: theme.typography.textSM.medium.fontWeight,
          "&:hover": {
            backgroundColor: theme.colors.primary50,
          },
        }}
        icon={
          <SvgIcon
            component={ICONS["arrow-up-right"]}
            sx={{
              width: "20px",
              height: "20px",
              marginRight: theme.spacing(0.5),
            }}
          />
        }
      />
    </FlexContainer>
  );

  const renderCardTitle = () => (
    <FlexContainer alignItems="center" gap={theme.spacing(1)}>
      <Text variant={"textMD"} color={"textSecondary"} weight={"medium"}>
        {t("dashboard.response-time.title")}
      </Text>
      <Tooltip tooltipContent={t("dashboard.response-time.help")}>
        <Container sx={{cursor: "help"}}>
          <SvgIcon
            component={ICONS["help-circle"]}
            sx={{
              width: "14px",
              height: "14px",
              color: theme.colors.gray400,
            }}
          />
        </Container>
      </Tooltip>
    </FlexContainer>
  );

  return (
    <>
      <SimpleCard
        cardBase={OutlinedCardBase}
        customContent={
          <FlexContainer
            width="100%"
            alignItems="flex-start"
            justifyContent="flex-start"
            flexDirection="column"
            gap={theme.spacing(2)}
          >
            <FlexContainer
              justifyContent="space-between"
              alignItems="flex-start"
              width="100%"
            >
              <FlexContainer flexDirection="column" gap={theme.spacing(2)}>
                {renderCardTitle()}
                {isMobile && selectedView === 0 && renderTimeframeSelector()}
              </FlexContainer>
              {renderControls()}
            </FlexContainer>

            {renderCardContent()}
          </FlexContainer>
        }
        sx={{
          height: "100%",
          width: "100%",
          padding: theme.spacing(5, 5, 3, 5),
          backgroundColor: theme.colors.white,
        }}
      />

      <ModalBase
        isOpen={modalOpen}
        onClose={handleModalClose}
        sx={{
          padding: theme.spacing(6),
          maxWidth: "800px",
          width: "90%",
          maxHeight: "80vh",
        }}
      >
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(3)}
          sx={{height: "100%"}}
        >
          <FlexContainer
            justifyContent="space-between"
            alignItems="flex-start"
            width="100%"
          >
            <FlexContainer flexDirection="column" gap={theme.spacing(2)}>
              {renderCardTitle()}
              {isMobile && selectedView === 0 && renderTimeframeSelector()}
            </FlexContainer>
            {renderControls(true)}
          </FlexContainer>
          <DataAvailabilityWarning />
          {selectedView === 0
            ? renderTimeseriesView(true)
            : renderUsersView(true)}
        </FlexContainer>
      </ModalBase>
    </>
  );
};

export default ResponseTimeCard;
